import { skipWaitingAndReload } from './swUpdate';

// Existing one-shot stale chunk recovery, extended to stylesheet failures.
// Such a failure can leave the PWA with plain HTML and no visible update banner.
const RELOAD_GUARD_KEY = 'chunkReloaded';
let recovering = false;

const isChunkLoadError = (value) => {
  if (!value) return false;
  const message = typeof value === 'string' ? value : value.message || value.name || '';
  return (
    /Loading chunk [\w-]+ failed/i.test(message) ||
    /Loading CSS chunk/i.test(message) ||
    message === 'ChunkLoadError'
  );
};

const isCssLink = (target) => {
  if (!target || target.tagName !== 'LINK' || !target.rel || !target.href) return false;
  try {
    const url = new URL(target.href, window.location.href);
    return target.rel.split(/\s+/).includes('stylesheet') &&
      url.origin === window.location.origin &&
      url.pathname.startsWith('/static/css/');
  } catch (error) {
    return false;
  }
};

const hasMissingStylesheets = () =>
  Array.from(document.querySelectorAll('link[rel~="stylesheet"]')).some(
    (link) => isCssLink(link) && !link.sheet
  );

// Never silently discard a form, conversation or payment in progress.
const isEditingFlow = () =>
  ['/intake', '/register', '/payment', '/payments', '/chats', '/discussion',
    '/edit-offer', '/edit-wish', '/account', '/settings'].some(
    (prefix) => window.location.pathname === prefix || window.location.pathname.startsWith(prefix + '/')
  );

const showRecoveryNotice = () => {
  if (!document.body || document.getElementById('jp-asset-recovery')) return;
  const dutch = document.documentElement.lang.toLowerCase().startsWith('nl');
  const wrapper = document.createElement('div');
  wrapper.id = 'jp-asset-recovery';
  wrapper.setAttribute('role', 'alert');
  wrapper.style.cssText = 'position:fixed;z-index:2147483647;left:12px;right:12px;bottom:16px;' +
    'padding:16px;border-radius:8px;background:#fff;color:#222;border:2px solid #178d91;' +
    'box-shadow:0 3px 20px #0004;font:16px/1.4 sans-serif;';
  const message = document.createElement('span');
  message.textContent = dutch
    ? 'De pagina is niet volledig geladen. Bewaar eventuele ingevulde gegevens en vernieuw de pagina.'
    : 'The page did not load completely. Save any entered information before refreshing.';
  const button = document.createElement('button');
  button.type = 'button';
  button.textContent = dutch ? 'Vernieuwen' : 'Refresh';
  button.style.cssText = 'margin-left:12px;padding:9px 14px;border-radius:5px;' +
    'border:0;background:#178d91;color:#fff;font:inherit;cursor:pointer;';
  button.addEventListener('click', () => {
    try { sessionStorage.removeItem(RELOAD_GUARD_KEY); } catch (error) {}
    recovering = false;
    reloadOnce(true);
  });
  wrapper.appendChild(message);
  wrapper.appendChild(button);
  document.body.appendChild(wrapper);
};

const reloadOnce = (manual = false) => {
  if (recovering) return;
  if (!manual && isEditingFlow()) {
    showRecoveryNotice();
    return;
  }
  try {
    if (sessionStorage.getItem(RELOAD_GUARD_KEY)) {
      showRecoveryNotice();
      return;
    }
    sessionStorage.setItem(RELOAD_GUARD_KEY, '1');
  } catch (error) {
    // Session storage may be unavailable; still attempt a single update.
  }
  recovering = true;
  Promise.resolve(skipWaitingAndReload()).catch(() => window.location.reload());
};

export const clearChunkReloadGuard = () => {
  if (typeof window === 'undefined' || document.readyState !== 'complete' || hasMissingStylesheets()) return;
  try {
    sessionStorage.removeItem(RELOAD_GUARD_KEY);
  } catch (error) {}
};

export const installChunkReloadHandler = () => {
  if (typeof window === 'undefined') return;

  const onResourceError = (event) => {
    if (isCssLink(event.target) ||
        isChunkLoadError(event && event.error) ||
        isChunkLoadError(event && event.message)) {
      reloadOnce();
    }
  };
  const onUnhandledRejection = (event) => {
    if (isChunkLoadError(event && event.reason)) reloadOnce();
  };

  window.addEventListener('error', onResourceError, true);
  window.addEventListener('unhandledrejection', onUnhandledRejection);

  // A stylesheet in the SSR <head> can fail before the JavaScript bundle
  // executes and before the error listener above is installed.
  const inspectInitialStylesheets = () => {
    if (hasMissingStylesheets()) reloadOnce();
    else clearChunkReloadGuard();
  };
  if (document.readyState === 'complete') inspectInitialStylesheets();
  else window.addEventListener('load', inspectInitialStylesheets, { once: true });

  return () => {
    window.removeEventListener('error', onResourceError, true);
    window.removeEventListener('unhandledrejection', onUnhandledRejection);
    window.removeEventListener('load', inspectInitialStylesheets);
  };
};
